import {
  inclusiveDaysBetween,
  parseCalendarDay,
  withDuration
} from "../../../DocumentTemplates/shared/duration-date-calculator.ts"

/**
 * A milestone's Planned Start, Planned End and Effort (`duration_days`), with
 * the dates as `YYYY-MM-DD` calendar days. Effort counts calendar days with
 * both ends included and is at least 1; `null` means not set.
 */
export interface MilestoneScheduleFields {
  start_date: string | null
  end_date: string | null
  duration_days: number | null
}

export type MilestoneScheduleField = keyof MilestoneScheduleFields

export const MILESTONE_SCHEDULE_FIELDS: MilestoneScheduleField[] = [
  "start_date",
  "end_date",
  "duration_days"
]

export type MilestoneScheduleProblem = "effortMismatch" | "endBeforeStart"

const endFor = (start: string, effort: number) =>
  withDuration({ startDate: start }, effort, "days").endDate ?? null

const startFor = (end: string, effort: number) =>
  withDuration({ endDate: end }, effort, "days").startDate ?? null

const effortFor = (start: string, end: string) => {
  const from = parseCalendarDay(start)
  const to = parseCalendarDay(end)
  if (!from || !to) return null
  const days = inclusiveDaysBetween(from, to)
  return days > 0 ? days : null
}

const usableEffort = (value: unknown): number | null => {
  const effort = Number(value)
  return value == null || value === "" || !Number.isFinite(effort) || effort < 1
    ? null
    : Math.floor(effort)
}

/**
 * The three fields after a write that changes `sent`, mirroring the server's
 * `resolve_schedule_fields` so the screen shows what the save will store
 * ("last edit wins"): the fields sent are never overwritten and the one left
 * out follows from the other two. Start alone keeps the Effort and moves the
 * end; end alone recalculates the Effort; Effort alone moves the end. Clearing
 * a date derives nothing; clearing Effort while both dates are set reads it
 * back from them. The server's answer stays the truth — this is the preview.
 */
export const resolveMilestoneSchedule = (
  stored: MilestoneScheduleFields,
  sent: Partial<MilestoneScheduleFields>
): MilestoneScheduleFields & { problem: MilestoneScheduleProblem | null } => {
  const has = (field: MilestoneScheduleField) => field in sent
  let start = has("start_date") ? (sent.start_date ?? null) : stored.start_date
  let end = has("end_date") ? (sent.end_date ?? null) : stored.end_date
  let effort = usableEffort(
    has("duration_days") ? sent.duration_days : stored.duration_days
  )
  let problem: MilestoneScheduleProblem | null = null

  if (has("start_date") && has("end_date") && has("duration_days")) {
    if (start && end && effort != null && effortFor(start, end) !== effort) {
      problem = "effortMismatch"
    }
  } else if (has("duration_days")) {
    if (effort == null) {
      if (start && end) effort = effortFor(start, end)
    } else if (has("start_date")) {
      if (start) end = endFor(start, effort)
    } else if (has("end_date")) {
      if (end) start = startFor(end, effort)
    } else if (start) {
      end = endFor(start, effort)
    } else if (end) {
      start = startFor(end, effort)
    }
  } else if (has("start_date") && has("end_date")) {
    if (start && end) effort = effortFor(start, end)
  } else if (has("start_date") && start) {
    if (effort != null) end = endFor(start, effort)
    else if (end) effort = effortFor(start, end)
  } else if (has("end_date") && end) {
    if (start) effort = effortFor(start, end)
    else if (effort != null) start = startFor(end, effort)
  }

  if (!problem && start && end) {
    const from = parseCalendarDay(start)
    const to = parseCalendarDay(end)
    if (from && to && to.isBefore(from)) problem = "endBeforeStart"
  }

  return {
    start_date: start,
    end_date: end,
    duration_days: effort,
    problem
  }
}

/** Effort is a whole number of days, at least 1; blank means not set. */
export const milestoneEffortRules = (message: string) => [
  {
    validator: (_: unknown, value: unknown) =>
      value == null || value === "" || Number(value) >= 1
        ? Promise.resolve()
        : Promise.reject(new Error(message))
  }
]

/**
 * The schedule fields a refused write names, read from the error envelope the
 * API sends (`details.<field>`, or `details.field_errors.<field>`), so they
 * can be shown on the field rather than only in the toast.
 */
export const milestoneScheduleFieldErrors = (
  payload: unknown
): Partial<Record<MilestoneScheduleField, string[]>> => {
  const details =
    (payload as { details?: Record<string, unknown> } | undefined)?.details ??
    {}
  const nested = (details.field_errors ?? {}) as Record<string, unknown>
  const errors: Partial<Record<MilestoneScheduleField, string[]>> = {}
  MILESTONE_SCHEDULE_FIELDS.forEach((field) => {
    const value = details[field] ?? nested[field]
    if (value == null || value === "") return
    errors[field] = (Array.isArray(value) ? value : [value]).map(String)
  })
  return errors
}

/**
 * An Effort-only milestone nothing can date: an Effort, no dates and nothing
 * it depends on. The server leaves it undated; it is listed, never drawn.
 */
export const isUnscheduledMilestone = (
  milestone: {
    start_date?: string | null
    end_date?: string | null
    duration_days?: number | null
  },
  hasPredecessor: boolean
) =>
  !milestone.start_date &&
  !milestone.end_date &&
  usableEffort(milestone.duration_days) != null &&
  !hasPredecessor
